import React, { useEffect, useState } from "react";
import axios from "axios";
import CIIOrbitDiagram from "./CIIOrbitDiagram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "ev_admin_token";
const PROPOSAL_PDF = "/downloads/EVG-2SFG-Proposal-Coupar-Angus.pdf";

const Lead = ({ children }) => <p className="wp-lead">{children}</p>;
const Pull = ({ children }) => <blockquote className="wp-pull">{children}</blockquote>;
const Bullets = ({ items }) => <ul className="wp-bullets">{items.map((t) => <li key={t}>{t}</li>)}</ul>;
const Sec = ({ eyebrow, title, children }) => (
  <section className="wp-sec">
    {eyebrow && <div className="wp-eyebrow">{eyebrow}</div>}
    <h2 className="wp-h2">{title}</h2>
    {children}
  </section>
);
const Flow = ({ steps }) => (
  <div className="pr-flow">
    {steps.map((s, i) => (
      <React.Fragment key={s + i}>
        <div className="pr-flow-step">{s}</div>
        {i < steps.length - 1 && <div className="pr-flow-arrow" aria-hidden="true">↓</div>}
      </React.Fragment>
    ))}
  </div>
);

export default function Proposal2SFG() {
  const [admin, setAdmin] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!t) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => setAdmin(true))
      .catch(() => setAdmin(false));
  }, []);

  return (
    <div className="wp-page-bg">
      <div className="wp-actions no-print">
        <a href="/" className="wp-back" data-testid="pr-back">← Back to site</a>
        <button className="wp-print" onClick={() => window.print()} data-testid="pr-print">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          Print / Save as PDF
        </button>
        {admin && (
          <a
            className="wp-print wp-download"
            href={PROPOSAL_PDF}
            download="EVG-2SFG-Proposal-Coupar-Angus.pdf"
            data-testid="pr-download"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            Download PDF (email-ready)
          </a>
        )}
      </div>

      <article className="wp-doc" data-testid="proposal-2sfg">
        {/* 1. COVER */}
        <header className="pr-cover" style={{ backgroundImage: "linear-gradient(180deg,rgba(11,21,51,.45),rgba(11,21,51,.68)),url('/brand/city_earth.jpg')" }}>
          <img className="wp-logo" src="/brand/logo.png" alt="Energy Vault Group" />
          <div className="wp-kicker">STRATEGIC ENERGY INTELLIGENCE PROPOSAL</div>
          <h1 className="pr-cover-title">2 Sisters Food Group</h1>
          <div className="pr-cover-site">Coupar Angus</div>
          <div className="pr-cover-prep">
            <div><span>Prepared for</span><b>Gary Cummings</b>2 Sisters Food Group</div>
            <div><span>Prepared by</span><b>Michael Fennell</b>Energy Vault Group</div>
          </div>
          <div className="wp-cover-steps">KNOWLEDGE BEFORE INVESTMENT&nbsp;&nbsp;•&nbsp;&nbsp;WE DO NOT ASSUME. WE MEASURE.</div>
          <div className="pr-cover-conf">PRIVATE &amp; CONFIDENTIAL — PREPARED EXCLUSIVELY FOR 2 SISTERS FOOD GROUP</div>
        </header>

        {/* 2. THANK YOU */}
        <Sec eyebrow="INTRODUCTION" title="Thank you for the opportunity">
          <p>We would like to thank 2 Sisters Food Group for the opportunity to engage with the team at Coupar Angus and begin exploring what we believe could become a significant energy-intelligence opportunity.</p>
          <p>The access, conversations and information provided so far have already allowed us to identify meaningful areas worthy of further investigation.</p>
          <p>This proposal sets out what we have identified, how we intend to prove it through measurement, how implementation could potentially be funded from the value recovered, and how the same infrastructure could ultimately support both the existing 2SFG estate and future developments.</p>
        </Sec>

        {/* 3. WHO WE ARE */}
        <Sec eyebrow="WHO WE ARE" title="More than 30 years in energy — now Integrated Energy Intelligence">
          <p>Energy Vault Group is led by <b>Michael Fennell</b>, bringing more than <b>30 years of experience</b> across the energy and renewable-energy sector.</p>
          <p>Our experience has evolved alongside the sector — through Solar PV, battery storage, EV infrastructure and energy systems, to the next stage: <span className="g">Integrated Energy Intelligence.</span></p>
          <p>Today our focus is not simply on supplying technology. We first understand how a building behaves, where energy is being unnecessarily consumed, what can be recovered and how intelligence can then be permanently integrated into the building.</p>
          <Pull>We do not assume. We measure.</Pull>
        </Sec>

        {/* PROOF BAR */}
        <section className="pr-proof" data-testid="pr-proof-bar">
          <div className="pr-proof-grid">
            <div className="pr-proof-item">
              <div className="pr-proof-num">30+ <span>YEARS</span></div>
              <div className="pr-proof-lab">Industry experience</div>
            </div>
            <div className="pr-proof-item">
              <div className="pr-proof-num">£192,600</div>
              <div className="pr-proof-lab">Annual energy-saving opportunity already identified at Coupar Angus</div>
              <div className="pr-proof-note">To be measured, verified and confirmed through the next stage of analysis.</div>
            </div>
            <div className="pr-proof-item">
              <div className="pr-proof-num">5.3 <span>BILLION</span></div>
              <div className="pr-proof-lab">Data points processed every day by the technology platform</div>
            </div>
            <div className="pr-proof-item">
              <div className="pr-proof-num">150%</div>
              <div className="pr-proof-lab">Performance guarantee on qualifying projects*</div>
            </div>
          </div>
          <div className="pr-proof-foot">*Subject to agreed scope, qualification and contractual terms.</div>
        </section>

        {/* 4. COUPAR ANGUS */}
        <Sec eyebrow="COUPAR ANGUS" title="What we know so far">
          <Lead>Coupar Angus is a large, complex, energy-intensive food-manufacturing operation.</Lead>
          <p>From the information provided so far, significant loads across the site include:</p>
          <Bullets items={[
            "Refrigeration and chiller systems",
            "Production lines and process equipment",
            "Compressed air / compressors",
            "Boilers and steam / heating systems",
            "Fans, extraction and ventilation",
            "Ancillary accommodation and welfare areas",
            "Battery and charging areas",
            "Other significant site loads",
          ]} />
          <Pull>We have still not received or analysed the complete asset inventory — so the current picture is not the finished analysis.</Pull>
        </Sec>

        {/* 5. THE COMMERCIAL HOOK — SAVINGS */}
        <section className="pr-hook">
          <div className="pr-hook-eyebrow">THE OPPORTUNITY ALREADY IDENTIFIED</div>
          <div className="pr-money">£192,600</div>
          <div className="pr-money-sub">of annual energy savings already identified at Coupar Angus</div>
          <div className="pr-money-note">Identified <b>before</b> complete asset-level monitoring and <b>before</b> analysis of the remaining asset inventory.</div>
          <div className="pr-money-evi">This opportunity has been identified from the site information, discussions and operational observations available to date. It precedes complete asset inventory, granular monitoring and full site diagnosis, and will therefore be subjected to measurement and verification before being presented as a confirmed saving.</div>
          <div className="pr-money-grid">
            <div className="pr-money-card"><span className="pr-mc-num">£192,600</span><span className="pr-mc-lab">1 Year</span></div>
            <div className="pr-money-card"><span className="pr-mc-num">£963,000</span><span className="pr-mc-lab">5 Years</span></div>
            <div className="pr-money-card"><span className="pr-mc-num">£1,926,000</span><span className="pr-mc-lab">10 Years</span></div>
          </div>
          <div className="pr-money-illus">The 5 and 10-year figures illustrate the value of a sustained £192,600 annual saving and are not presented as guaranteed outcomes.</div>
          <div className="pr-hook-line">If this is what is already <span className="g">visible</span> — what remains <span className="g">hidden?</span></div>
          <div className="pr-hook-sub">We do not turn observations into assumptions. The next stage is to measure, quantify and verify.</div>
        </section>

        {/* INSERT: MEASURED. VERIFIED. GUARANTEED. */}
        <section className="pr-guarantee">
          <div className="pr-hook-eyebrow">DE-RISKING THE PROPOSITION</div>
          <h2 className="pr-guar-title">Measured. Verified. <span className="g">Guaranteed.</span></h2>
          <p className="pr-guar-p">2SFG should not be asked to make an investment decision based on assumptions, projections or generic industry percentages. Our approach is to establish the baseline, measure actual energy behaviour and quantify the opportunity from evidence.</p>
          <div className="pr-guar-badge">
            <div className="pr-guar-num">150%</div>
            <div className="pr-guar-lab">Performance Guarantee</div>
            <div className="pr-guar-terms">On qualifying verified savings, subject to agreed scope and contractual terms.</div>
          </div>
          <div className="pr-guar-three">
            <span>WE DO NOT ASSUME. WE MEASURE.</span>
            <span>WE DO NOT SIMPLY PROJECT SAVINGS. WE VERIFY THEM.</span>
            <span>AND QUALIFYING VERIFIED SAVINGS CAN BE GUARANTEED.</span>
          </div>
          <p className="pr-guar-p">The purpose of the guarantee is to reduce commercial risk. 2SFG is not simply being presented with a theoretical business case; the process moves from identification to measurement, verification and, where applicable, guaranteed performance.</p>
        </section>

        {/* 6. WHY CONTINUE */}
        <Sec eyebrow="THE COMMERCIAL LOGIC" title="Why there is a strong case to continue">
          <p>Energy Vault has already identified a potential opportunity of considerable value <b>without having the complete picture.</b></p>
          <Bullets items={[
            "We have not yet fully interrogated every significant asset.",
            "We have not yet created granular visibility across the operation.",
            "We have not yet applied the complete digital intelligence infrastructure.",
          ]} />
          <Pull>Stopping at this stage risks making a decision before the evidence has been fully uncovered.</Pull>
        </Sec>

        {/* 7. PROVE IT ON ONE ASSET */}
        <Sec eyebrow="RISK REDUCTION" title="Prove it on one asset first">
          <Lead>2SFG does not need to accept our assumptions — because we are not asking them to.</Lead>
          <Flow steps={["Agree one suitable asset or operational area", "Establish its baseline", "Measure its actual energy behaviour", "Identify waste", "Implement the agreed intervention", "Measure again", "Verify the result", "Then make the next decision from evidence"]} />
        </Sec>

        {/* 8. NO NEW MONEY */}
        <section className="pr-hook alt">
          <div className="pr-hook-eyebrow">THE COMMERCIAL MODEL</div>
          <div className="pr-money2">Potentially no new money required</div>
          <div className="pr-hook-line" style={{ marginTop: 14 }}>Use the cost of waste to help <span className="g">fund the removal of waste.</span></div>
          <p className="pr-guar-p">Once savings are measured and verified, the programme can potentially be structured so that money currently leaving the business through avoidable energy waste is redirected towards the infrastructure required to remove that waste.</p>
          <Flow steps={["IDENTIFY", "MEASURE", "VERIFY", "FUND", "IMPLEMENT", "RETAIN THE SAVING"]} />
          <div className="pr-hook-sub">Subject to the final project, financial approval and commercial structure — creating the potential for implementation without requiring equivalent new expenditure over and above money already being lost through waste.</div>
        </section>

        {/* 9. FINANCE & IMPLEMENTATION OPTIONS */}
        <Sec eyebrow="FINANCE & IMPLEMENTATION OPTIONS" title="Flexibility in how implementation is structured">
          <p>Energy Vault Group has access to its own finance facilities and, as we would expect, 2 Sisters Food Group will have established internal funding and finance routes.</p>
          <p>The purpose of this proposal is therefore not to prescribe how investment should be funded, but to provide flexibility around implementation.</p>
          <p>Projects can be structured through:</p>
          <ul className="wp-bullets">
            <li><b>CAPEX</b> — direct capital investment.</li>
            <li><b>OPEX</b> — structured operational expenditure.</li>
            <li><b>Energy Vault Group finance facilities</b> — where appropriate.</li>
            <li><b>2SFG&rsquo;s own finance or funding facilities.</b></li>
            <li><b>Savings-led implementation</b> — where verified energy savings can help offset, or potentially fund, the cost of implementation.</li>
          </ul>
          <p>The most appropriate route can be determined once the opportunity has been fully measured, verified and commercially assessed.</p>
          <Pull>Identify the waste. Measure the opportunity. Verify the savings. Then determine the most commercially appropriate route to implementation.</Pull>
        </Sec>

        {/* 9B. ILLUSTRATIVE FINANCIAL CASE */}
        <section className="wp-sec pr-fin" data-testid="pr-fin-case">
          <div className="wp-eyebrow">ILLUSTRATIVE FINANCIAL CASE</div>
          <h2 className="wp-h2">Conventional CAPEX Route</h2>
          <div className="pr-fin-badge">For illustration purposes only</div>
          <p>The following example demonstrates how a conventional CAPEX-funded implementation could potentially perform at Coupar Angus, based on the opportunity identified to date.</p>
          <h3 className="pr-fin-h3">Illustrative Financial Model</h3>
          <table className="wp-table">
            <thead><tr><th>Financial Measure</th><th>Illustrative Figure</th></tr></thead>
            <tbody>
              <tr><td>Total Installed CAPEX</td><td>£128,500 + VAT</td></tr>
              <tr><td>VAT @ 20%</td><td>£25,700</td></tr>
              <tr><td>Total Including VAT</td><td>£154,200</td></tr>
              <tr><td>Gross Energy Saving</td><td>£16,050 per month</td></tr>
              <tr><td>Virtual Energy Management (VEM)</td><td>£1,000 per month</td></tr>
              <tr><td>Net Saving Retained by 2SFG</td><td>£15,050 per month</td></tr>
              <tr><td>Net Annual Saving</td><td>£180,600</td></tr>
              <tr><td>Simple Payback</td><td>Approx. 8.5 months</td></tr>
            </tbody>
          </table>
          <h3 className="pr-fin-h3">Illustrative Long-Term Value</h3>
          <div className="pr-fin-cards">
            <div><span>£52,100</span>Year 1 net value after initial CAPEX</div>
            <div><span>£774,500</span>5-year net value after initial CAPEX</div>
            <div><span>£1,677,500</span>10-year net value after initial CAPEX</div>
          </div>
          <Pull>The illustration demonstrates the potential for the initial capital investment to be recovered within the first year, followed by a continuing positive financial contribution to the business.</Pull>
        </section>

        {/* 9C. VEM + 150% ASSURANCE */}
        <Sec eyebrow="VIRTUAL ENERGY MANAGEMENT" title="Protecting the savings, not just finding them">
          <p>For an operation of the scale and complexity of Coupar Angus, Virtual Energy Management forms an important part of the ongoing energy-intelligence strategy.</p>
          <p>The objective is not simply to identify savings and implement measures, but to continuously monitor performance, identify anomalies, protect implemented savings and identify further opportunities.</p>
          <Flow steps={["MEASURE", "VERIFY", "IMPLEMENT", "MONITOR", "PROTECT", "OPTIMISE"]} />
          <div className="pr-calc">
            <div className="pr-calc-row"><span className="pr-calc-num">£192,600</span><span className="pr-calc-lab">Gross Annual Saving</span></div>
            <div className="pr-calc-op">less</div>
            <div className="pr-calc-row"><span className="pr-calc-num">£12,000</span><span className="pr-calc-lab">Annual Virtual Energy Management</span></div>
            <div className="pr-calc-op">leaves</div>
            <div className="pr-calc-row net"><span className="pr-calc-num">£180,600</span><span className="pr-calc-lab">Net Annual Saving to 2SFG</span></div>
            <div className="pr-calc-op">which is</div>
            <div className="pr-calc-row"><span className="pr-calc-num">£15,050</span><span className="pr-calc-lab">Net Saving per Month</span></div>
          </div>
          <div className="pr-ns-block">
            <h3 className="pr-fin-h3">150% Performance Guarantee</h3>
            <p>Where measured and verified savings qualify under the applicable guarantee, they can be supported by a 150% Performance Guarantee, subject to the agreed contractual terms, baseline and measurement-and-verification methodology.</p>
            <p>This provides an additional layer of commercial assurance around qualifying verified performance.</p>
          </div>
          <div className="pr-ns-tag">WE DO NOT ASSUME. WE MEASURE.</div>
        </Sec>

        {/* 9D. FLEXIBLE FUNDING OPTIONS */}
        <Sec eyebrow="FLEXIBLE FUNDING OPTIONS" title="Structured around the most appropriate commercial route">
          <p>Energy Vault Group has access to a number of funding and finance options, allowing implementation to be structured around the most appropriate commercial route for the project.</p>
          <p>These can include:</p>
          <Bullets items={[
            "CAPEX",
            "OPEX",
            "Funded Implementation Models",
            "Energy Vault Group Finance Facilities",
            "2SFG\u2019s Own Internal Finance Facilities",
          ]} />
          <Pull>The objective is to ensure that a commercially viable project is not delayed simply because of the method of funding.</Pull>
          <p>The final funding structure can be agreed once the savings opportunity has been fully measured, verified and the final project scope established.</p>
        </Sec>

        {/* 9E. ILLUSTRATIVE CAPEX SUMMARY — dark statement page */}
        <section className="pr-hook pr-finsum" data-testid="pr-capex-summary">
          <div className="pr-hook-eyebrow">ILLUSTRATIVE CAPEX SUMMARY</div>
          <div className="pr-finflow">
            <div className="pr-ff"><span className="pr-ff-num">£128,500 + VAT</span><span className="pr-ff-lab">Initial Investment</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff"><span className="pr-ff-num">£192,600</span><span className="pr-ff-lab">Gross Annual Saving</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff"><span className="pr-ff-num">£12,000</span><span className="pr-ff-lab">Annual Virtual Energy Management</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff big"><span className="pr-ff-num">£180,600</span><span className="pr-ff-lab">Net Annual Saving</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff big"><span className="pr-ff-num">APPROX. 8.5-MONTH</span><span className="pr-ff-lab">Simple Payback</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff"><span className="pr-ff-num">£774,500</span><span className="pr-ff-lab">Net Value After CAPEX — 5 Years</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff big"><span className="pr-ff-num">£1.678 MILLION</span><span className="pr-ff-lab">Net Value After CAPEX — 10 Years</span></div>
            <div className="pr-ff-arrow">↓</div>
            <div className="pr-ff guar"><span>QUALIFYING VERIFIED SAVINGS SUPPORTED BY THE 150% PERFORMANCE GUARANTEE</span></div>
          </div>
          <div className="pr-money-illus" style={{ maxWidth: 680 }}>Illustrative financial example only. Figures are provided solely to demonstrate how a conventional CAPEX model could operate and do not constitute a quotation, forecast or guaranteed level of saving. Final CAPEX, VEM charges, verified savings, payback and guarantee coverage would be established following detailed measurement, verification, final system design and agreed commercial terms.</div>
        </section>

        {/* 10. DIGITALISATION */}
        <Sec eyebrow="HOW WE DELIVER IT" title="Digitalisation">
          <Lead>Turning previously invisible operational behaviour into live, measurable information.</Lead>
          <p>Digitalisation provides the foundation to understand:</p>
          <Bullets items={["Where energy is being consumed.", "When it is being consumed.", "How individual assets are behaving.", "Where abnormal or avoidable consumption is occurring."]} />
          <Pull>It takes 2SFG beyond simply receiving a monthly utility bill.</Pull>
        </Sec>

        {/* 11. DNS */}
        <Sec eyebrow="DNS" title="The Digital Nervous System">
          <Lead>The Digital Nervous System creates the sensing and communication layer throughout the building.</Lead>
          <p>Monitoring points, assets and systems continuously communicate their behaviour. In the same way that the human nervous system communicates what is happening throughout the body, the DNS allows the building to continuously reveal what is happening throughout its operation.</p>
          <Pull>A building cannot become intelligent until it can first sense what is happening inside it.</Pull>
        </Sec>

        {/* 11B. DNS & CNS — THE DISTINCTION */}
        <section className="wp-sec pr-dnscns" data-testid="pr-dnscns">
          <div className="wp-eyebrow">DNS & CNS — TWO DISTINCT LAYERS</div>
          <h2 className="wp-h2">The network that connects, and the layer that coordinates</h2>
          <p><b>The Digital Nervous System (DNS)</b> is the network that connects the building, its assets, meters, plant, machinery and operational systems and allows live information to flow.</p>
          <p><b>The Central Nervous System (CNS)</b> is the central coordination layer where that information is brought together and managed as one connected intelligence environment.</p>
          <p>From there, <b>CII</b> structures the intelligence, <b>AI</b> analyses and interprets it, and <b>VEM</b> provides the continuous human oversight needed to act on it and protect performance.</p>
          <Pull>These are not separate products or optional features. They are interconnected layers of one complete service, and the full Energy Vault proposition depends on them working together.</Pull>
          <div className="pr-arch">
            <div className="pr-arch-row top"><span>BUILDING &amp; ASSETS</span></div>
            <div className="pr-arch-arrow">↓</div>
            <div className="pr-arch-row"><span>DIGITAL NERVOUS SYSTEM (DNS)</span><em>Connects and carries live information</em></div>
            <div className="pr-arch-arrow">↓</div>
            <div className="pr-arch-row"><span>CENTRAL NERVOUS SYSTEM (CNS)</span><em>Brings everything together and coordinates the environment</em></div>
            <div className="pr-arch-arrow">↓</div>
            <div className="pr-arch-row"><span>CENTRALISED INTELLIGENCE INFRASTRUCTURE (CII)</span><em>Structures and centralises the intelligence</em></div>
            <div className="pr-arch-arrow">↓</div>
            <div className="pr-arch-row"><span>ARTIFICIAL INTELLIGENCE (AI)</span><em>Analyses, detects and interprets</em></div>
            <div className="pr-arch-arrow">↓</div>
            <div className="pr-arch-row"><span>VIRTUAL ENERGY MANAGEMENT (VEM)</span><em>Continuous human oversight, action and optimisation</em></div>
          </div>
          <div className="pr-dns-banner">
            <div className="pr-dns-big">THE DNS CONNECTS THE BUILDING.<br /><span className="g">THE CNS BRINGS EVERYTHING TOGETHER.</span></div>
            <div className="pr-dns-sub">One building. One connected intelligence environment. One central point of coordination.</div>
          </div>
        </section>

        {/* 12. CII */}
        <Sec eyebrow="CII" title="Centralised Intelligence Infrastructure">
          <Lead>DNS creates the signals. CNS brings everything together. CII structures the intelligence.</Lead>
          <div className="wp-diagram"><CIIOrbitDiagram /></div>
          <p>CII creates the central infrastructure through which asset, energy and operational information can be viewed and understood.</p>
          <div className="pr-scale">
            <div><span>At Coupar Angus</span>Asset → Area → Department → Site</div>
            <div><span>Ultimately across 2SFG</span>Site → Multiple Sites → Group</div>
          </div>
          <Pull>This is the foundation for centralised, estate-level energy intelligence.</Pull>
        </Sec>

        {/* 13. AI — INTELLIGENCE AT SCALE */}
        <Sec eyebrow="AI" title="Intelligence at scale">
          <Lead>This is not conventional monthly energy reporting.</Lead>
          <div className="wp-stat">
            <div className="wp-stat-big">5.3 BILLION</div>
            <div className="wp-stat-sub">data points processed every day by the technology platform</div>
          </div>
          <p>This scale of data allows the system to continually analyse energy behaviour across connected assets and buildings — identifying patterns, abnormalities, changes in consumption and opportunities that would be extremely difficult to detect manually.</p>
          <Pull>The purpose is not simply to collect more data. It is to turn billions of individual measurements into actionable intelligence.</Pull>
          <p>For 2SFG this creates the potential to move from retrospective reporting toward a much more proactive operating environment:</p>
          <Bullets items={[
            "What is happening?",
            "Where is it happening?",
            "Why has behaviour changed?",
            "What requires attention?",
            "Where is the next saving opportunity?",
          ]} />
          <div className="pr-ai-arch">
            <div><span>Digitalisation</span>captures the behaviour.</div>
            <div><span>DNS</span>connects the building.</div>
            <div><span>CNS</span>brings everything together.</div>
            <div><span>CII</span>structures the intelligence.</div>
            <div><span>AI</span>interrogates the data.</div>
            <div><span>VEM</span>turns intelligence into ongoing action.</div>
          </div>
        </Sec>

        {/* 14. VEM */}
        <Sec eyebrow="VEM" title="Virtual Energy Management">
          <Lead>Technology is only one part of the solution. VEM adds the ongoing specialist human-management layer.</Lead>
          <p>Performance can be reviewed continuously, anomalies investigated and opportunities acted upon. The objective is to prevent identified savings simply disappearing again as operational circumstances change.</p>
          <div className="wp-stat"><div className="wp-stat-big">24 / 7 / 365</div><div className="wp-stat-sub">energy intelligence and continuous improvement</div></div>
        </Sec>

        {/* 15. CARBON / ESG */}
        <Sec eyebrow="REPORTING" title="Carbon, ESG & audit-ready reporting">
          <Lead>The same measured infrastructure can support energy, carbon and ESG reporting, performance verification and audit-ready records.</Lead>
          <p>This is an important additional business benefit. It moves reporting away from repeatedly reconstructing historical information towards a continuous, evidence-based data environment.</p>
        </Sec>

        {/* 16. EXISTING BUILDINGS */}
        <Sec eyebrow="EXISTING BUILDINGS" title="Find the waste already built into the operation">
          <Flow steps={["Measure it", "Understand it", "Eliminate what can be eliminated", "Create the intelligence infrastructure to help prevent it returning"]} />
          <Pull>This is the Coupar Angus opportunity.</Pull>
        </Sec>

        {/* 17. NEW BUILDINGS */}
        <Sec eyebrow="NEW BUILDINGS" title="Design out the waste">
          <p>2SFG will continue to invest in new facilities, extensions, refurbishments, replacement plant and other capital projects. That creates an opportunity to move from finding waste after construction to preventing avoidable waste being designed in from the beginning.</p>
          <p>Energy Vault can potentially become involved at design stage to consider digitalisation architecture, metering strategy, DNS, CNS, CII, controls, asset-level visibility, AI readiness, carbon/ESG reporting and integration of Solar PV, storage and future technologies.</p>
          <div className="pr-twoup">
            <div><b>Existing buildings</b>Find and eliminate the waste.</div>
            <div><b>New buildings</b>Prevent the waste being built in.</div>
          </div>
          <Pull>Why build tomorrow&rsquo;s buildings with yesterday&rsquo;s energy intelligence?</Pull>
        </Sec>

        {/* 17B. NEW BUILDINGS — FULL PAGE */}
        <section className="wp-sec pr-newbuild" data-testid="pr-newbuild-page">
          <div className="wp-eyebrow">NEW BUILDINGS</div>
          <h2 className="wp-h2">Prevent the waste before it is built in</h2>
          <Lead>The opportunity is not only to identify and remove energy waste from 2SFG&rsquo;s existing estate.</Lead>
          <p>Future factories, extensions, refurbishments and major infrastructure projects give 2SFG the opportunity to build energy intelligence into the design from day one.</p>
          <p>By integrating Digitalisation, the Digital Nervous System (DNS), the Central Nervous System (CNS), Centralised Intelligence Infrastructure (CII), Artificial Intelligence (AI) and Virtual Energy Management (VEM) at design stage, 2SFG can establish the correct monitoring, metering, controls and intelligence infrastructure before the building becomes operational.</p>
          <div className="pr-hflow">
            <div className="pr-hflow-step">Digitalisation</div><div className="pr-hflow-arrow">→</div>
            <div className="pr-hflow-step">Digital Nervous System (DNS)</div><div className="pr-hflow-arrow">→</div>
            <div className="pr-hflow-step">Central Nervous System (CNS)</div><div className="pr-hflow-arrow">→</div>
            <div className="pr-hflow-step">Centralised Intelligence Infrastructure (CII)</div><div className="pr-hflow-arrow">→</div>
            <div className="pr-hflow-step">Artificial Intelligence (AI)</div><div className="pr-hflow-arrow">→</div>
            <div className="pr-hflow-step">Virtual Energy Management (VEM)</div>
          </div>
          <div className="pr-duo">
            <div className="pr-duo-good">
              <h3>Benefits of doing this at design stage</h3>
              <ul>
                <li>Energy visibility from commissioning</li>
                <li>Correct sub-metering and asset-level monitoring designed in rather than retrofitted</li>
                <li>Identification of potential waste before it becomes normal operating behaviour</li>
                <li>Better-informed specification of refrigeration, HVAC, compressors, motors, controls and other major loads</li>
                <li>A foundation for Artificial Intelligence, predictive intelligence and Virtual Energy Management</li>
                <li>Audit-ready Carbon and ESG reporting from the outset</li>
                <li>Solar PV, Battery Storage and EV infrastructure sized against a properly understood energy strategy</li>
                <li>The potential for one common energy-intelligence architecture across future 2SFG developments</li>
              </ul>
            </div>
            <div className="pr-duo-bad">
              <h3>The consequence of not doing it</h3>
              <p className="pr-duo-lead">If energy intelligence is considered only after construction, 2SFG risks building tomorrow&rsquo;s facilities with yesterday&rsquo;s energy infrastructure.</p>
              <ul>
                <li>Waste can become embedded into normal operation</li>
                <li>Metering, sensors and controls may later require retrofitting</li>
                <li>Systems can remain fragmented</li>
                <li>Energy inefficiencies may only become visible after significant operating cost has already been incurred</li>
              </ul>
            </div>
          </div>
          <blockquote className="wp-pull pr-nb-break">Why spend years identifying and correcting tomorrow the energy waste that could have been prevented at design stage today?</blockquote>
          <Pull>The cheapest energy waste to eliminate is the waste that was never built in.</Pull>
          <div className="pr-twoup">
            <div><b>Existing buildings</b>Find it. Measure it. Eliminate it. Prevent it returning.</div>
            <div><b>New buildings</b>Prevent the waste from being built in at all.</div>
          </div>
        </section>

        {/* 18. SOLAR & BATTERY */}
        <Sec eyebrow="GENERATION & STORAGE" title="Solar PV & Battery Storage">
          <Lead>Solar and batteries remain important — but they should not lead this proposal. Diagnosis comes first.</Lead>
          <p>Once we understand the building&rsquo;s actual energy behaviour, we can determine where generation, battery storage and other technologies will deliver the strongest return.</p>
          <Pull>No amount of Solar PV or Battery Storage can identify hidden energy waste by itself. First understand the building. Then invest intelligently.</Pull>
        </Sec>

        {/* 19. ESTATE SCALE */}
        <Sec eyebrow="SCALABILITY" title="From Coupar Angus to the wider 2SFG estate">
          <Flow steps={["ONE ASSET", "ONE OPERATIONAL AREA", "COUPAR ANGUS", "ADDITIONAL 2SFG SITES", "CENTRALISED GROUP INTELLIGENCE"]} />
          <p>Coupar Angus becomes the proof point, not the ceiling of the opportunity. We are not assuming every 2SFG site will deliver identical savings — what becomes scalable is the methodology and infrastructure:</p>
          <Pull>Measure → Diagnose → Verify → Implement → Monitor → Learn → Scale.</Pull>
        </Sec>

        {/* 20. COMPLETE VISION */}
        <section className="pr-hook">
          <div className="pr-hook-eyebrow">THE COMPLETE 2SFG VISION</div>
          <Flow steps={[
            "£192,600 annual savings already identified",
            "Prove through measurement",
            "Qualifying verified performance guaranteed",
            "Use recovered value / funding to implement",
            "Digitalisation",
            "Digital Nervous System (DNS)",
            "Central Nervous System (CNS)",
            "Centralised Intelligence Infrastructure (CII)",
            "Artificial Intelligence (AI)",
            "Virtual Energy Management (VEM)",
            "Carbon + ESG + Audit-Ready Reporting",
            "EXISTING ESTATE — Identify → Measure → Eliminate → Control",
            "NEW DEVELOPMENTS — Design → Digitalise → Connect → Prevent",
            "ONE 2SFG ENERGY-INTELLIGENCE STRATEGY",
          ]} />
          <div className="pr-hook-sub">This is not simply a proposal to reduce an electricity bill. It is a strategy to understand, control and continually improve the energy behaviour of the organisation — while creating an intelligence infrastructure capable of supporting both today&rsquo;s estate and tomorrow&rsquo;s buildings.</div>
        </section>

        {/* 21. THE NEXT STEP — CLOSING PAGE */}
        <section className="wp-sec pr-nextstep" data-testid="pr-nextstep">
          <div className="wp-eyebrow">THE NEXT STEP</div>
          <h2 className="wp-h2">From Coupar Angus to a 2SFG Energy-Intelligence Strategy</h2>
          <Lead>This proposal is not simply about reducing energy consumption at one site.</Lead>
          <p>It is about establishing an evidence-led energy-intelligence framework that can support 2SFG&rsquo;s existing estate, future refurbishments, extensions and new developments.</p>

          <div className="pr-ns-block">
            <h3>Existing estate</h3>
            <div className="pr-ns-flow">Identify → Measure → Diagnose → Verify → Eliminate → Control → Scale</div>
            <p>Begin with an agreed operational area at Coupar Angus. Establish the baseline, measure actual energy behaviour and verify the opportunity through evidence.</p>
            <p>Once proven, the same methodology and intelligence infrastructure can be progressively deployed across other suitable areas and sites within the 2SFG estate.</p>
          </div>

          <div className="pr-ns-block">
            <h3>New builds, extensions &amp; refurbishments</h3>
            <div className="pr-ns-flow">Design → Digitalise → Connect → Prevent → Optimise</div>
            <p>Where 2SFG is planning new factories, extensions, refurbishments or major infrastructure projects, Energy Vault Group can become involved at design stage — before energy waste is built in.</p>
            <p>This creates the opportunity to specify the correct metering, sub-metering, controls and intelligence infrastructure from the outset, incorporating:</p>
            <div className="pr-ns-flow">Digitalisation → Digital Nervous System (DNS) → Central Nervous System (CNS) → Centralised Intelligence Infrastructure (CII) → Artificial Intelligence (AI) → Virtual Energy Management (VEM)</div>
            <p>rather than attempting to retrofit that intelligence after the building is operational.</p>
          </div>

          <div className="pr-ns-block">
            <h3>Implementation</h3>
            <p>Once savings and opportunities have been measured and verified, implementation can be structured around the most appropriate commercial route, including CAPEX, OPEX, Energy Vault Group finance facilities or 2SFG&rsquo;s own finance facilities.</p>
          </div>

          <div className="pr-ns-block">
            <h3>One 2SFG energy-intelligence strategy</h3>
            <div className="pr-twoup">
              <div><b>Existing Buildings</b>Find the waste. Measure it. Eliminate it. Control it.</div>
              <div><b>New Buildings</b>Prevent the waste from being designed in.</div>
            </div>
            <p>The immediate next step is simple: agree the first suitable operational area at Coupar Angus for measurement, while identifying any forthcoming new-build, extension or refurbishment projects where energy intelligence can be considered from design stage.</p>
          </div>

          <div className="pr-ns-tag">WE DO NOT ASSUME. WE MEASURE.</div>
        </section>

        <footer className="wp-end">
          <img className="wp-logo" src="/brand/logo.png" alt="Energy Vault Group" />
          <div className="wp-end-meta" style={{ marginTop: 20 }}>Energy Vault Group&nbsp;&nbsp;|&nbsp;&nbsp;Strategic Energy Intelligence Proposal&nbsp;&nbsp;|&nbsp;&nbsp;2 Sisters Food Group — Coupar Angus</div>
        </footer>
      </article>
    </div>
  );
}
